/* ==========================================================================
   Gallery Premium — shared JS
   Powers: PDP (main-product-starter-kit, via layout/theme.liquid)
           Homepage buy-box (home-hero-product, via layout/theme.liquid)

   Works against the generic .product--thumbnail_slider / .product--thumbnail
   gallery markup that both sections share, so one file improves both.

   Features:
   1. Eager-load + LQIP resolve for the active slide (existing)
   2. Preload the immediate neighbor slides (prev/next) so advancing never
      shows a loading blur
   3. Desktop mouse drag/swipe on the main image (arrows still work)
   4. Active thumbnail is always auto-scrolled into view
   5. One-time mobile "swipe hint" nudge on first load
   6. Self-healing slide counter (recomputes total/current from the DOM)
   ========================================================================== */
(function () {
  'use strict';

  var GALLERY_SELECTOR = '.product--thumbnail_slider, .product--thumbnail';
  var DESKTOP_QUERY = '(min-width: 750px)';
  var DRAG_THRESHOLD = 40;
  var SWIPE_HINT_KEY = 'ig_swipe_hint_shown';

  function ready(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn);
    } else {
      fn();
    }
  }

  /* ---- 1. LQIP resolve + eager load ---- */
  function markLoaded(img) {
    var wrap = img.closest('.lqip-media');
    if (wrap) wrap.classList.add('is-loaded');
  }

  function eagerLoad(img) {
    if (!img) return;
    if (img.getAttribute('loading') === 'lazy') img.loading = 'eager';
    if (img.complete && img.naturalWidth > 0) {
      markLoaded(img);
    } else {
      img.addEventListener('load', function () { markLoaded(img); }, { once: true });
    }
  }

  function loadAllImagesIn(item) {
    if (!item || !item.querySelectorAll) return;
    item.querySelectorAll('img').forEach(eagerLoad);
  }

  /* ---- 2. Preload neighbor slides ---- */
  function getSiblingMediaItems(item) {
    var list = item.parentElement;
    if (!list) return [];
    return Array.prototype.filter.call(list.children, function (el) {
      return el.classList && el.classList.contains('product__media-item');
    });
  }

  function preloadNeighbors(item) {
    var items = getSiblingMediaItems(item);
    var idx = items.indexOf(item);
    if (idx === -1 || items.length < 2) return;
    var n = items.length;
    [(idx + 1) % n, (idx - 1 + n) % n].forEach(function (neighborIdx) {
      loadAllImagesIn(items[neighborIdx]);
    });
  }

  /* ---- 4. Active thumbnail auto-scroll ----
     Deliberately avoids Element.scrollIntoView(): on this gallery it fights
     with Dawn's own slider-component scroll tracking and can cascade into
     a very large number of redundant calls. Scrolling the list directly via
     scrollTo() is inert with respect to that tracking and idempotent.
     A short defer is required: Dawn's own thumbnail-click handling briefly
     resets the thumbnail list's scrollLeft right after the click; scrolling
     immediately gets silently overwritten, so we wait it out. */
  function scrollThumbIntoView(item) {
    var mediaId = item.getAttribute('data-media-id');
    if (!mediaId) return;
    var root = item.closest(GALLERY_SELECTOR);
    if (!root) return;

    setTimeout(function () {
      var list = root.querySelector('.thumbnail-list');
      var thumb = list && list.querySelector('.thumbnail-list__item[data-target="' + mediaId + '"]');
      if (!thumb || !list) return;

      var listRect = list.getBoundingClientRect();
      var thumbRect = thumb.getBoundingClientRect();
      var delta = (thumbRect.left + thumbRect.width / 2) - (listRect.left + listRect.width / 2);
      if (Math.abs(delta) < 2) return;

      var maxScroll = list.scrollWidth - list.clientWidth;
      var target = Math.max(0, Math.min(maxScroll, list.scrollLeft + delta));
      if (list.scrollTo) {
        list.scrollTo({ left: target, behavior: 'smooth' });
      } else {
        list.scrollLeft = target;
      }
    }, 120);
  }

  /* ---- 6. Self-healing counter ---- */
  function fixCounterTotal(root) {
    var totalEl = root.querySelector('.slider-counter--total');
    if (!totalEl) return;
    var total = root.querySelectorAll('.product__media-item').length;
    if (total > 0 && totalEl.textContent.trim() !== String(total)) {
      totalEl.textContent = total;
    }
  }

  function updateCounterCurrent(item) {
    var root = item.closest(GALLERY_SELECTOR);
    if (!root) return;
    var items = getSiblingMediaItems(item);
    var idx = items.indexOf(item);
    var currentEl = root.querySelector('.slider-counter--current');
    if (currentEl && idx > -1) currentEl.textContent = idx + 1;
    fixCounterTotal(root);
  }

  /* ---- Combined "slide became active" handler ---- */
  function onSlideActivated(item) {
    loadAllImagesIn(item);
    preloadNeighbors(item);
    scrollThumbIntoView(item);
    updateCounterCurrent(item);
  }

  /* ---- 3. Desktop drag / swipe ---- */
  function initDrag(root) {
    var wrapper = root.querySelector('.product__media-wrapper');
    if (!wrapper || wrapper.dataset.dragInit) return;
    wrapper.dataset.dragInit = '1';

    var dragging = false;
    var moved = false;
    var startX = 0;
    var startY = 0;

    function suppressNextClick() {
      var handler = function (ev) {
        ev.preventDefault();
        ev.stopPropagation();
        wrapper.removeEventListener('click', handler, true);
      };
      wrapper.addEventListener('click', handler, true);
    }

    wrapper.addEventListener('pointerdown', function (e) {
      if (e.pointerType && e.pointerType !== 'mouse') return; // touch keeps native scroll swipe
      if (!window.matchMedia(DESKTOP_QUERY).matches) return;
      dragging = true;
      moved = false;
      startX = e.clientX;
      startY = e.clientY;
    });

    wrapper.addEventListener('pointermove', function (e) {
      if (!dragging) return;
      var dx = e.clientX - startX;
      var dy = e.clientY - startY;
      if (!moved && Math.abs(dx) > 8 && Math.abs(dx) > Math.abs(dy)) {
        moved = true;
        wrapper.classList.add('is-dragging');
      }
    });

    function endDrag(e) {
      if (!dragging) return;
      dragging = false;
      wrapper.classList.remove('is-dragging');
      if (!moved) return;
      var dx = e.clientX - startX;
      if (Math.abs(dx) > DRAG_THRESHOLD) {
        var btn = root.querySelector(dx < 0 ? '.slider-button--next' : '.slider-button--prev');
        if (btn) btn.click();
      }
      suppressNextClick();
    }

    wrapper.addEventListener('pointerup', endDrag);
    wrapper.addEventListener('pointerleave', function () {
      if (dragging && !moved) dragging = false;
    });
    wrapper.addEventListener('dragstart', function (e) { e.preventDefault(); });
  }

  /* ---- 5. Mobile swipe hint (once per session) ---- */
  function initSwipeHint() {
    if (window.matchMedia(DESKTOP_QUERY).matches) return;
    if (sessionStorage.getItem(SWIPE_HINT_KEY)) return;

    var wrapper = document.querySelector(GALLERY_SELECTOR + ' .product__media-wrapper');
    if (!wrapper || wrapper.dataset.hintInit) return;
    wrapper.dataset.hintInit = '1';

    var hint = document.createElement('div');
    hint.className = 'gallery-swipe-hint';
    hint.setAttribute('aria-hidden', 'true');
    hint.innerHTML = '<span class="gallery-swipe-hint__icon">&#8596;</span>';
    wrapper.appendChild(hint);

    var dismissed = false;
    function dismiss() {
      if (dismissed) return;
      dismissed = true;
      hint.classList.add('is-hidden');
      setTimeout(function () { hint.remove(); }, 400);
      try { sessionStorage.setItem(SWIPE_HINT_KEY, '1'); } catch (e) {}
    }

    wrapper.addEventListener('touchstart', dismiss, { once: true, passive: true });
    setTimeout(dismiss, 2400);
  }

  /* ---- Boot ---- */
  ready(function () {
    document.querySelectorAll(GALLERY_SELECTOR).forEach(function (root) {
      fixCounterTotal(root);
      initDrag(root);
    });

    document.querySelectorAll(
      '.product--thumbnail_slider .product__media-item.is-active, .product--thumbnail .product__media-item.is-active'
    ).forEach(onSlideActivated);

    var mo = new MutationObserver(function (mutations) {
      mutations.forEach(function (m) {
        if (
          m.attributeName === 'class' &&
          m.target.classList &&
          m.target.classList.contains('product__media-item') &&
          m.target.classList.contains('is-active')
        ) {
          onSlideActivated(m.target);
        }
      });
    });

    document.querySelectorAll('.product--thumbnail_slider .product__media-item, .product--thumbnail .product__media-item').forEach(function (item) {
      mo.observe(item, { attributes: true, attributeFilter: ['class'] });
    });

    initSwipeHint();
  });
})();
